import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import {
  Controller,
  FieldValues,
  FormProvider,
  useForm,
  UseFormSetError,
} from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  useAddNewContactMutation,
  useUpdateContactMutation,
} from "../../../../api/services/client/clientApis";
import {
  AddNewContactParams,
  ContactDetailsParams,
  ContactProfileParams,
  ErrorModel,
  GetUpdateContactModel,
} from "../../../../api/services/client/models";
import {
  AccordionHeader,
  AccordionHeaderText,
  FormController,
  MuiBox,
  MuiCheckbox,
  MuiGrid,
  MuiModal,
  MuiMultiSelect,
  MuiSelect,
  MuiSelectPhoneWithTextInput,
  MuiTextField,
  MuiTypography,
  ToastDisplayType,
  WarningConfirmationModal,
} from "../../../../components";
import { STATUS_BADGE_TYPE } from "../../../../components/statusBadge/StatusBadge";
import {
  BE_ERROR_CODES,
  INPUT_LIMIT,
  MAX_LENGTH_100,
  PAGE_IDS,
} from "../../../../helpers/constants";
import { COMPONENT_IDS } from "../../../../helpers/constants/componentIds";
import { commonSiteCoreKeys } from "../../../../helpers/constants/siteCoreKeys";
import {
  emailValidation,
  getParsedBEErrors,
  handlePasteValidationOnSpecialChars,
  hasKey,
  isEmpty,
  isNotEmpty,
  replaceHashWithValue,
} from "../../../../helpers/utils";
import { useMultiSelectARIALabels } from "../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import { createClientSelector } from "../../../../redux/slices/client/createClientSelector";
import { AddContactFormKey } from "../../modelsAndSchemas/Models";
import { AddNewContactValidationSchema } from "../../modelsAndSchemas/ValidationSchema";
import { createClientSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import {
  AddNewContactWrapper,
  ConfirmatoryPopupWrapper,
} from "./AddNewContact.style";

export interface AddNewContactProps {
  open: boolean;
  onClose: () => void;
  onContactAdded?: (contactId?: string) => void;
  isEdit?: boolean;
  contactData?: GetUpdateContactModel;
  billingCountry?: string;
  isAttachedToCurrentClient: boolean;
}

export const AddNewContact: FC<AddNewContactProps> = ({
  open,
  onClose,
  onContactAdded,
  isEdit = false,
  contactData,
  isAttachedToCurrentClient,
}) => {
  const pageId = PAGE_IDS.ADD_NEW_CONTACT;
  const gridSize = { lg: 4, md: 6, xs: 12, sm: 6 };
  const contactTypeRef = useRef<{ resetValues: (values?: string[]) => void }>(
    null,
  );

  const { loggedInUser } = useGetLoggedInUserDetails();
  const dispatch = useDispatch();
  const {
    fetchLabel,
    commonLabels,
    fetchDropdownValues,
    fetchComponentTitle,
    fetchValidationMessage,
    commonValidationMessages,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const { id: clientId } = useSelector(createClientSelector);
  const [showConfirmatoryPopup, setShowConfirmatoryPopup] = useState(false);
  const [showUploadWarningPopup, setShowUploadWarningPopup] = useState(false);
  const confirmatoryMessageRef = useRef<string>("");
  const buttonClickedRef = useRef<string>("");

  const {
    addContactTitle,
    editContactTitle,
    addContactProfileHeading,
    prefixLabel,
    firstNameLabel,
    lastNameLabel,
    jobTitleLabel,
    telePhoneLabel,
    mobileLabel,
    workEmailLabel,
    surveyLanguageLabel,
    statusLabel,
    contactDetailsHeading,
    contactTypeLabel,
    selectedContactTypesLabel,
    surveyContactLabel,
    contactSuccessfullyAddedMessage,
    contactSuccessfullyUpdatedMessage,
    surveyLanguageDropdown,
    prefixDropdown,
    statusDropdown,
    contactTypeDropdown,
    cancelActionConfirmLabel,
    addActionConfirmLabel,
    editCancelActionConfirmLabel,
    updateActionConfirmLabel,
    modifyContactTypeMessage,
    pleaseChooseAtLeastOneContactTypeOrMarkThisContactAsASurveyContact,
    emailAlreadyInUseError,
    noteMessage,
    modifyContactStatusMessage,
    contactTypeShouldNotBeUpdatedMessage,
  } = createClientSiteCoreKeys;

  const getLabel = (key: string) => {
    return fetchLabel(addContactTitle, key);
  };

  const cancelMessage = getLabel(cancelActionConfirmLabel);
  const saveMessage = getLabel(addActionConfirmLabel);
  const editCancelMessage = getLabel(editCancelActionConfirmLabel);
  const updateMessage = getLabel(updateActionConfirmLabel);

  const buttonSave = commonLabels.save;
  const buttonCancel = commonLabels.cancel;
  const buttonUpdate = commonLabels.update;

  const methods = useForm({
    mode: "onBlur",
    defaultValues: {
      [AddContactFormKey.prefix]: "",
      [AddContactFormKey.firstName]: "",
      [AddContactFormKey.lastName]: "",
      [AddContactFormKey.jobTitle]: "",
      [AddContactFormKey.telephoneCountryCode]: "",
      [AddContactFormKey.telephoneNumber]: "",
      [AddContactFormKey.mobileCountryCode]: "",
      [AddContactFormKey.mobileNumber]: "",
      [AddContactFormKey.workEmail]: "",
      [AddContactFormKey.surveyLanguage]: "",
      [AddContactFormKey.status]: "",
      [AddContactFormKey.contactType]: [],
      [AddContactFormKey.isSurveyContact]: false,
      [AddContactFormKey.telephoneISOCode]: "",
      [AddContactFormKey.mobileISOCode]: "",
    },
    resolver: yupResolver(
      AddNewContactValidationSchema(
        fetchValidationMessage,
        fetchLabel,
        commonValidationMessages,
      ),
    ),
  });

  const {
    formState: { isValid, dirtyFields, errors },
    control,
    getValues,
    handleSubmit,
    setValue,
    setError,
  } = methods;

  const {
    mutateAsync: addNewContactApi,
    data,
    isPending,
    isSuccess,
    isError,
    error: addContactApiError,
  } = useAddNewContactMutation();

  const {
    mutateAsync: updateContactApi,
    data: updateData,
    isPending: isUpdateContactPending,
    isSuccess: isUpdateContactSuccess,
    isError: isUpdateContactError,
    error: updateContactApiError,
  } = useUpdateContactMutation();

  useEffect(() => {
    if (!isEdit) {
      setValue(AddContactFormKey.status, STATUS_BADGE_TYPE.active);
    }
  }, [isEdit]);

  useEffect(() => {
    if (isEdit && contactData) {
      const { contactProfile, contactDetails } = contactData;
      setValue(AddContactFormKey.prefix, contactProfile.prefixId);
      setValue(AddContactFormKey.firstName, contactProfile.firstName);
      setValue(AddContactFormKey.lastName, contactProfile.lastName);
      setValue(AddContactFormKey.jobTitle, contactProfile.jobTitle);
      setValue(
        AddContactFormKey.telephoneCountryCode,
        contactProfile.telephoneCountryCode,
      );
      setValue(
        AddContactFormKey.telephoneNumber,
        contactProfile.telephoneNumber,
      );
      setValue(
        AddContactFormKey.telephoneISOCode,
        contactProfile.telephoneISOCode,
      );
      setValue(
        AddContactFormKey.mobileCountryCode,
        contactProfile.mobileCountryCode,
      );
      setValue(AddContactFormKey.mobileNumber, contactProfile.mobileNumber);
      setValue(AddContactFormKey.mobileISOCode, contactProfile.mobileISOCode);
      setValue(AddContactFormKey.workEmail, contactProfile.workEmail);
      setValue(
        AddContactFormKey.surveyLanguage,
        contactProfile.surveyLanguageId,
      );
      setValue(AddContactFormKey.status, contactProfile.status);
      setValue(AddContactFormKey.contactType, contactDetails?.contactTypeId);
      setValue(
        AddContactFormKey.isSurveyContact,
        !!contactDetails?.isSurveyContact,
      );

      setTimeout(() => {
        contactTypeRef.current?.resetValues(contactDetails?.contactTypeId);
      }, 100);
    }
  }, [isEdit, contactData]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isPending || isUpdateContactPending,
      }),
    );
  }, [isPending, isUpdateContactPending]);

  useEffect(() => {
    if (isUpdateContactSuccess && updateData) {
      onClose();
      dispatch(
        toggleToast({
          open: true,
          message: getLabel(contactSuccessfullyUpdatedMessage),
          type: "success",
        }),
      );
      onContactAdded?.(contactData?.contactId);
    }
  }, [updateData, isUpdateContactSuccess]);

  useEffect(() => {
    if (isSuccess && data) {
      onClose();
      dispatch(
        toggleToast({
          open: true,
          message: getLabel(contactSuccessfullyAddedMessage),
          type: "success",
        }),
      );
      onContactAdded?.();
    }
  }, [data, isSuccess]);

  const dispatchToast = (message: string, type?: ToastDisplayType) => {
    dispatch(
      toggleToast({
        open: true,
        message,
        type,
      }),
    );
  };

  const [
    restrictUserEditContactTypeErrorMessage,
    setRestrictUserEditContactTypeErrorMessage,
  ] = useState("");

  const [warningErrorMessage, setWarningErrorMessage] = useState("");

  const setCustomErrorBeneathTheField = () => {
    setError(AddContactFormKey.workEmail, {
      type: "custom",
      message: getLabel(emailAlreadyInUseError),
    });
  };

  const showErrorsFromBE = (error: ErrorModel) => {
    switch (error.errorCode) {
      case BE_ERROR_CODES.CONTACT_UPLOAD_WARNING_CODE:
        setWarningErrorMessage(getLabel(modifyContactTypeMessage));
        setShowUploadWarningPopup(true);
        break;
      case BE_ERROR_CODES.RESTRICT_USER_EDIT_CONTACT_TYPE:
        setRestrictUserEditContactTypeErrorMessage(
          getLabel(
            pleaseChooseAtLeastOneContactTypeOrMarkThisContactAsASurveyContact,
          ),
        );
        break;
      case BE_ERROR_CODES.CONTACT_EMAIL_ALREADY_IN_USE:
        setCustomErrorBeneathTheField();
        break;
      case BE_ERROR_CODES.CONTACT_ALREADY_IN_USE_BILLING_CONFIG:
        setWarningErrorMessage(getLabel(modifyContactStatusMessage));
        setShowUploadWarningPopup(true);
        break;
      case BE_ERROR_CODES.CONTACT_ALREADY_MAPPED_TO_PROGRAM_PROJECT:
        setWarningErrorMessage(getLabel(contactTypeShouldNotBeUpdatedMessage));
        setShowUploadWarningPopup(true);
        break;
    }
  };

  useEffect(() => {
    if (
      (isError && addContactApiError) ||
      (isUpdateContactError && updateContactApiError)
    ) {
      const errorObj = isError ? addContactApiError : updateContactApiError;
      const error = getParsedBEErrors(errorObj)[0];
      if (isNotEmpty(error) && hasKey(error, "errorCode")) {
        showErrorsFromBE(error);
      } else {
        const errorMessage =
          error && hasKey(error, "message")
            ? error?.message
            : commonLabels.someThingWentWrongPleaseTryAgainLater;
        dispatchToast(errorMessage, "error");
      }
    }
  }, [
    isError,
    addContactApiError,
    isUpdateContactError,
    updateContactApiError,
  ]);

  const getValuesFromForm = () => {
    const {
      prefix,
      firstName,
      lastName,
      jobTitle,
      workEmail,
      telephoneNumber,
      telephoneCountryCode,
      telephoneISOCode,
      mobileNumber,
      mobileCountryCode,
      mobileISOCode,
      contactType,
      isSurveyContact,
      status = STATUS_BADGE_TYPE.inActive,
      surveyLanguage,
    } = getValues();

    let contactProfile: ContactProfileParams = {
      firstName,
      lastName,
      workEmail,
      status,
      surveyLanguageId: surveyLanguage,
    };
    let contactDetails: ContactDetailsParams = {};

    if (isNotEmpty(prefix)) {
      contactProfile = {
        ...contactProfile,
        prefixId: prefix,
      };
    }
    if (isNotEmpty(jobTitle)) {
      contactProfile = {
        ...contactProfile,
        jobTitle,
      };
    }
    if (isNotEmpty(telephoneNumber)) {
      contactProfile = {
        ...contactProfile,
        telephoneNumber,
        telephoneCountryCode,
        telephoneISOCode,
      };
    }
    if (isNotEmpty(mobileNumber)) {
      contactProfile = {
        ...contactProfile,
        mobileNumber,
        mobileCountryCode,
        mobileISOCode,
      };
    }
    if (isNotEmpty(contactType)) {
      contactDetails = {
        ...contactDetails,
        contactTypeId: contactType?.filter(
          (type): type is string => type !== undefined,
        ),
      };
    }
    if (isNotEmpty(isSurveyContact)) {
      contactDetails = {
        ...contactDetails,
        isSurveyContact: isSurveyContact,
      };
    }
    const params: AddNewContactParams = {
      clientId,
      contactProfile,
      contactDetails,
    };

    return params;
  };

  const checkFieldValidations = () => {
    if (isValid) {
      handleConfirmatoryPopup(
        isEdit && contactData ? buttonUpdate : buttonSave,
      );
    }
  };

  const handleConfirmatoryPopup = (buttonTitle: string) => {
    buttonClickedRef.current = buttonTitle;
    if (buttonTitle === buttonSave) {
      confirmatoryMessageRef.current = saveMessage;
    } else if (buttonTitle === buttonCancel) {
      confirmatoryMessageRef.current = isEdit
        ? editCancelMessage
        : cancelMessage;
    } else if (buttonTitle === buttonUpdate) {
      confirmatoryMessageRef.current = updateMessage;
    }
    if (!showConfirmatoryPopup) {
      setShowConfirmatoryPopup(true);
    }
  };

  const clearRefs = () => {
    buttonClickedRef.current = "";
    confirmatoryMessageRef.current = "";
  };

  const hideConfirmatoryPopup = () => {
    if (showConfirmatoryPopup) {
      setShowConfirmatoryPopup(false);
      clearRefs();
    }
  };

  const handleWarningPopup = () => {
    if (showUploadWarningPopup) {
      setShowUploadWarningPopup(false);
      onClose();
    }
  };

  const callApiAndDismissModals = () => {
    if (buttonClickedRef.current === buttonSave) {
      setShowConfirmatoryPopup(false);
      clearRefs();

      if (getValuesFromForm()) {
        addNewContactApi(getValuesFromForm());
      }
    } else if (buttonClickedRef.current === buttonUpdate) {
      setShowConfirmatoryPopup(false);
      clearRefs();
      if (contactData) {
        const updatedParams: GetUpdateContactModel = {
          contactId: contactData.contactId,
          ...getValuesFromForm(),
        };

        if (updatedParams) {
          updateContactApi(updatedParams);
        }
      }
    } else {
      setShowConfirmatoryPopup(false);
      onClose();
      clearRefs();
    }
  };

  const getCountryIsoCode = (key: string) => {
    return isEdit
      ? ((contactData?.contactProfile as ContactProfileParams)?.[
          key as keyof ContactProfileParams
        ] as string)
      : loggedInUser?.countryCode;
  };

  const handleOnClose = () => {
    if (isEdit && isEmpty(dirtyFields)) {
      if (
        !!contactData?.contactDetails?.isSurveyContact !==
        getValues(AddContactFormKey.isSurveyContact)
      ) {
        handleConfirmatoryPopup(buttonCancel);
      } else {
        onClose();
      }
    } else if (isNotEmpty(dirtyFields)) {
      handleConfirmatoryPopup(buttonCancel);
    } else {
      onClose();
    }
  };

  const renderHeader = (label: string) => {
    return <AccordionHeaderText label={label} />;
  };

  const getFullName = () =>
    `${contactData?.contactProfile.lastName}, ${contactData?.contactProfile.firstName}`;

  const modalTitle = useMemo(() => {
    return isEdit
      ? replaceHashWithValue(getLabel(editContactTitle), getFullName())
      : fetchComponentTitle(addContactTitle);
  }, [isEdit]);

  const modalPrimaryButtonTitle = isEdit ? buttonUpdate : buttonSave;

  const isContactOrSurveyFieldUpdated =
    isEdit &&
    !isAttachedToCurrentClient &&
    (dirtyFields.contactType || dirtyFields.isSurveyContact);

  const handlePaste = (
    formKey: string,
    e: React.ClipboardEvent<HTMLDivElement>,
  ) =>
    handlePasteValidationOnSpecialChars(
      e,
      formKey,
      INPUT_LIMIT.ALPHABETIC_WITH_SPACES_AND_HYPHEN_REGEX,
      setError as UseFormSetError<FieldValues>,
      fetchValidationMessage(commonSiteCoreKeys.specialCharNotAllowed),
      false,
    );

  return (
    <FormProvider {...methods}>
      <MuiModal
        id={COMPONENT_IDS.ADD_NEW_CONTACT_MODAL}
        open={open}
        onClose={handleOnClose}
        title={modalTitle}
        hasLargeContent
        primaryButtonTitle={modalPrimaryButtonTitle}
        secondaryButtonTitle={buttonCancel}
        onSecondaryButtonClick={() => handleConfirmatoryPopup(buttonCancel)}
        onPrimaryButtonClick={() => handleSubmit(checkFieldValidations)()}
        footerErrorMessage={restrictUserEditContactTypeErrorMessage}
        className="add-new-contact-modal"
      >
        <AddNewContactWrapper>
          <AccordionHeader
            title={renderHeader(getLabel(addContactProfileHeading))}
            showExpandCollapseIcon={false}
          >
            <MuiBox className="content">
              <MuiGrid container spacing={2}>
                <MuiGrid size={gridSize}>
                  <FormController
                    control={control}
                    name={AddContactFormKey.prefix}
                  >
                    <MuiSelect
                      id={`${pageId}-${AddContactFormKey.prefix}`}
                      label={getLabel(prefixLabel)}
                      visibleDefaultSelectOption
                      placeholder={commonLabels.selectPlaceHolder}
                      options={fetchDropdownValues(
                        addContactTitle,
                        prefixDropdown,
                      )}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>
                <MuiGrid size={gridSize}>
                  <FormController
                    control={control}
                    name={AddContactFormKey.firstName}
                  >
                    <MuiTextField
                      isMandatory
                      id={`${pageId}-${AddContactFormKey.firstName}`}
                      label={getLabel(firstNameLabel)}
                      placeholder={commonLabels.inputPlaceHolder}
                      maxLength={MAX_LENGTH_100}
                      requiredARIALabel={commonARIALabel.required}
                      tooltipMessage={
                        commonValidationMessages.fieldSpecialCharactersTooltipMessage
                      }
                      tooltipHeaderTitle={
                        commonValidationMessages.allowedSpecialCharactersTitle
                      }
                    />
                  </FormController>
                </MuiGrid>
                <MuiGrid size={gridSize}>
                  <FormController
                    control={control}
                    name={AddContactFormKey.lastName}
                  >
                    <MuiTextField
                      id={`${pageId}-${AddContactFormKey.lastName}`}
                      isMandatory
                      label={getLabel(lastNameLabel)}
                      placeholder={commonLabels.inputPlaceHolder}
                      maxLength={MAX_LENGTH_100}
                      // inputProps={nameWithSpacesAndDashesValidation}
                      requiredARIALabel={commonARIALabel.required}
                      tooltipMessage={
                        commonValidationMessages.fieldSpecialCharactersTooltipMessage
                      }
                      tooltipHeaderTitle={
                        commonValidationMessages.allowedSpecialCharactersTitle
                      }
                    />
                  </FormController>
                </MuiGrid>
                <MuiGrid size={gridSize}>
                  <FormController
                    control={control}
                    name={AddContactFormKey.jobTitle}
                  >
                    <MuiTextField
                      id={`${pageId}-${AddContactFormKey.jobTitle}`}
                      label={getLabel(jobTitleLabel)}
                      placeholder={commonLabels.inputPlaceHolder}
                      maxLength={MAX_LENGTH_100}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>
                <MuiGrid size={gridSize}>
                  <FormController
                    control={control}
                    name={AddContactFormKey.telephoneNumber}
                  >
                    <MuiSelectPhoneWithTextInput
                      label={getLabel(telePhoneLabel)}
                      defaultSelectedCountry={getCountryIsoCode(
                        AddContactFormKey.telephoneISOCode,
                      )}
                      placeholder={commonLabels.telephonePlaceHolder}
                      defaultValue={
                        isEdit
                          ? getValues(AddContactFormKey.telephoneNumber)
                          : ""
                      }
                      hideFormattingAndDisableValidation={isEdit}
                      phoneNumberFormId={AddContactFormKey.telephoneNumber}
                      countryCodeFormId={AddContactFormKey.telephoneCountryCode}
                      countryIsoCodeFormId={AddContactFormKey.telephoneISOCode}
                      searchPlaceholder={commonLabels.search}
                      searchNotFound={commonLabels.noResult}
                      selectLabel={commonLabels.selectPlaceHolder}
                      invalidPhoneNumberMessage={
                        isEdit
                          ? commonValidationMessages.phoneNumberMinimumOf3DigitsRequiredMessage
                          : commonValidationMessages.invalidPhoneNumber
                      }
                    />
                  </FormController>
                </MuiGrid>
                <MuiGrid size={gridSize}>
                  <FormController
                    control={control}
                    name={AddContactFormKey.mobileNumber}
                  >
                    <MuiSelectPhoneWithTextInput
                      label={getLabel(mobileLabel)}
                      defaultSelectedCountry={getCountryIsoCode(
                        AddContactFormKey.mobileISOCode,
                      )}
                      placeholder={commonLabels.telephonePlaceHolder}
                      defaultValue={
                        isEdit ? getValues(AddContactFormKey.mobileNumber) : ""
                      }
                      phoneNumberFormId={AddContactFormKey.mobileNumber}
                      hideFormattingAndDisableValidation={isEdit}
                      countryCodeFormId={AddContactFormKey.mobileCountryCode}
                      countryIsoCodeFormId={AddContactFormKey.mobileISOCode}
                      searchPlaceholder={commonLabels.search}
                      searchNotFound={commonLabels.noResult}
                      selectLabel={commonLabels.selectPlaceHolder}
                      invalidPhoneNumberMessage={
                        isEdit
                          ? commonValidationMessages.phoneNumberMinimumOf3DigitsRequiredMessage
                          : commonValidationMessages.invalidPhoneNumber
                      }
                    />
                  </FormController>
                </MuiGrid>

                <MuiGrid size={gridSize}>
                  <FormController
                    control={control}
                    name={AddContactFormKey.workEmail}
                  >
                    <MuiTextField
                      isMandatory
                      id={`${pageId}-${AddContactFormKey.workEmail}`}
                      label={getLabel(workEmailLabel)}
                      placeholder={commonLabels.emailPlaceHolder}
                      slotProps={{
                        htmlInput: {
                          ...emailValidation,
                          "aria-invalid": !!errors.workEmail,
                        },
                      }}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>
                <MuiGrid size={gridSize}>
                  <FormController
                    control={control}
                    name={AddContactFormKey.surveyLanguage}
                  >
                    <MuiSelect
                      id={`${pageId}-${AddContactFormKey.surveyLanguage}`}
                      isMandatory
                      label={getLabel(surveyLanguageLabel)}
                      options={fetchDropdownValues(
                        addContactTitle,
                        surveyLanguageDropdown,
                      )}
                      visibleDefaultSelectOption
                      placeholder={commonLabels.selectPlaceHolder}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>
                <MuiGrid size={gridSize}>
                  <FormController
                    control={control}
                    name={AddContactFormKey.status}
                  >
                    <MuiSelect
                      id={`${pageId}-${AddContactFormKey.status}`}
                      label={getLabel(statusLabel)}
                      disabled={!isEdit}
                      options={fetchDropdownValues(
                        addContactTitle,
                        statusDropdown,
                      )}
                      requiredARIALabel={commonARIALabel.required}
                      placeholder={commonLabels.selectPlaceHolder}
                    />
                  </FormController>
                </MuiGrid>
              </MuiGrid>
            </MuiBox>
          </AccordionHeader>
          <AccordionHeader
            title={renderHeader(getLabel(contactDetailsHeading))}
            showExpandCollapseIcon={false}
          >
            <MuiBox className="content">
              <MuiGrid container spacing={2} className="contact-type-container">
                <MuiGrid size={gridSize}>
                  <FormController
                    control={control}
                    name={AddContactFormKey.contactType}
                    ref={contactTypeRef}
                  >
                    <MuiMultiSelect
                      id={`${pageId}-${AddContactFormKey.contactType}`}
                      label={getLabel(contactTypeLabel)}
                      options={fetchDropdownValues(
                        addContactTitle,
                        contactTypeDropdown,
                      )}
                      showChip={true}
                      showSelectAllOption
                      dialogTitle={getLabel(selectedContactTypesLabel)}
                      selectPlaceHolder={commonLabels.selectPlaceHolder}
                      searchPlaceHolder={commonLabels.search}
                      selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                      doneText={commonLabels.done}
                      allARIAProps={multiSelectARIALabels}
                    />
                  </FormController>
                </MuiGrid>
                <MuiGrid size={gridSize} className="checkbox-container">
                  <MuiBox>
                    <Controller
                      name={AddContactFormKey.isSurveyContact}
                      control={control}
                      render={({ field: props }) => (
                        <MuiCheckbox
                          {...props}
                          checked={props.value}
                          checkboxLabel={getLabel(surveyContactLabel)}
                          id={`${pageId}-${AddContactFormKey.isSurveyContact}`}
                          onCheckedChange={(checked: boolean) => {
                            props.onChange(checked);
                          }}
                        />
                      )}
                    />
                  </MuiBox>
                </MuiGrid>
              </MuiGrid>
            </MuiBox>
          </AccordionHeader>
          <MuiBox />
          {showConfirmatoryPopup && (
            <MuiModal
              id={COMPONENT_IDS.CONFIRMATION_MODAL}
              onClose={hideConfirmatoryPopup}
              title={commonLabels.confirmation}
              open={showConfirmatoryPopup}
              onPrimaryButtonClick={callApiAndDismissModals}
              onSecondaryButtonClick={hideConfirmatoryPopup}
              secondaryButtonTitle={commonLabels.no}
              primaryButtonTitle={commonLabels.yes}
            >
              <ConfirmatoryPopupWrapper>
                <MuiTypography
                  id={`${pageId}-confirmation-message`}
                  variant="h3"
                  className="confirmatory-message"
                >
                  {confirmatoryMessageRef.current}
                </MuiTypography>

                {isContactOrSurveyFieldUpdated && (
                  <MuiBox className="confirmatory-message-note-container">
                    <MuiTypography
                      id={`${pageId}-confirmation-message-note`}
                      variant="h3Bold"
                      className="confirmatory-message"
                    >
                      {commonLabels.note}
                    </MuiTypography>
                    <MuiTypography
                      id={`${pageId}-confirmation-message-note`}
                      variant="h3"
                      className="note-message"
                    >
                      {getLabel(noteMessage)}
                    </MuiTypography>
                  </MuiBox>
                )}
              </ConfirmatoryPopupWrapper>
            </MuiModal>
          )}
          {showUploadWarningPopup && (
            <WarningConfirmationModal
              show={showUploadWarningPopup}
              onClose={() => {
                setShowUploadWarningPopup(false);
              }}
              onPrimaryButtonClick={handleWarningPopup}
              showSecondaryButton={false}
              primaryButtonTitle={commonLabels.ok}
              labelContent={warningErrorMessage}
            />
          )}
        </AddNewContactWrapper>
      </MuiModal>
    </FormProvider>
  );
};
